import React, { useState, useEffect } from 'react';

const CustomPromptModal = ({
  isOpen,
  onClose,
  customPrompts,
  onSavePrompts,
  onProcessItems,
  isProcessing,
  itemsCount = 0,
  aiProvider,
  onAiProviderChange,
  extraContent = null,
  autoSortByAisle = false,
  onAutoSortToggle,
  completedLast = false,
  onCompletedLastToggle
}) => {
  // Normalize prompts to always be objects with {id, propertyName, text, color}
  const normalizePrompts = (prompts) => {
    if (!prompts || prompts.length === 0) return [];

    return prompts.map((p, index) => {
      if (typeof p === 'string') {
        // Legacy string format
        return {
          id: `legacy_${Date.now()}_${index}`,
          propertyName: `property_${index}`,
          text: p,
          color: '#3b82f6'
        };
      }

      // Object format - ensure all required fields exist
      const normalized = {
        id: p.id || `prompt_${Date.now()}_${index}`,
        propertyName: p.propertyName || extractPropertyNameFromText(p.text) || `property_${index}`,
        text: p.text || '',
        color: p.color || '#3b82f6'
      };

      console.log(`[Normalize] Prompt ${index}:`, { input: p, output: normalized });
      return normalized;
    });
  };

  // Helper to extract property name from old-style prompt text
  const extractPropertyNameFromText = (text) => {
    if (!text) return null;
    const match = text.match(/property called ['"]([^'"]+)['"]/i);
    return match ? match[1].toLowerCase() : null;
  };

  const [prompts, setPrompts] = useState(normalizePrompts(customPrompts));
  const [newPrompt, setNewPrompt] = useState('');
  const [newPromptPropertyName, setNewPromptPropertyName] = useState('');
  const [newPromptColor, setNewPromptColor] = useState('#3b82f6');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingPropertyName, setEditingPropertyName] = useState('');
  const [editingColor, setEditingColor] = useState('#3b82f6');
  const [sortByAisle, setSortByAisle] = useState(autoSortByAisle);
  const [moveCompletedLast, setMoveCompletedLast] = useState(completedLast);

  useEffect(() => {
    setPrompts(normalizePrompts(customPrompts));
  }, [customPrompts]);

  useEffect(() => {
    setSortByAisle(autoSortByAisle);
  }, [autoSortByAisle]);

  useEffect(() => {
    setMoveCompletedLast(completedLast);
  }, [completedLast]);

  const handleAddPrompt = () => {
    if (newPrompt.trim() && newPromptPropertyName.trim()) {
      setPrompts([...prompts, {
        id: `prompt_${Date.now()}_${prompts.length}`,
        propertyName: newPromptPropertyName.trim().toLowerCase(),
        text: newPrompt.trim(),
        color: newPromptColor
      }]);
      setNewPrompt('');
      setNewPromptPropertyName('');
      // Cycle to next color
      const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];
      const currentIndex = colors.indexOf(newPromptColor);
      setNewPromptColor(colors[(currentIndex + 1) % colors.length]);
    }
  };

  const handleRemovePrompt = (index) => {
    setPrompts(prompts.filter((_, i) => i !== index));
  };

  const handleEditPrompt = (index) => {
    setEditingIndex(index);
    const prompt = prompts[index];
    setEditingValue(prompt.text || '');
    setEditingPropertyName(prompt.propertyName || '');
    setEditingColor(prompt.color || '#3b82f6');
  };

  const handleSaveEdit = () => {
    if (editingValue.trim() && editingPropertyName.trim() && editingIndex !== null) {
      const newPrompts = [...prompts];
      const existingPrompt = newPrompts[editingIndex];
      newPrompts[editingIndex] = {
        ...existingPrompt,
        propertyName: editingPropertyName.trim().toLowerCase(),
        text: editingValue.trim(),
        color: editingColor
      };
      setPrompts(newPrompts);
    }
    setEditingIndex(null);
    setEditingValue('');
    setEditingPropertyName('');
    setEditingColor('#3b82f6');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleSave = async () => {
    await onSavePrompts(prompts);
    if (onAutoSortToggle) {
      onAutoSortToggle(sortByAisle);
    }
    if (onCompletedLastToggle) {
      onCompletedLastToggle(moveCompletedLast);
    }
    onClose();
  };

  const handleProcessItems = async () => {
    if (prompts.length === 0) {
      alert('Please add at least one prompt before processing items.');
      return;
    }

    if (itemsCount === 0) {
      alert('No items found in this list. Please add some items before processing with custom prompts.');
      return;
    }

    try {
      await onSavePrompts(prompts);
      await new Promise(resolve => setTimeout(resolve, 500));
      await onProcessItems();
    } catch (error) {
      console.error('Failed to process items:', error);
      const errorMessage = error.response?.data?.message || error.message;
      alert('Failed to process items with custom prompts: ' + errorMessage);
    }
  };

  const handleSortToggle = () => {
    setSortByAisle(!sortByAisle);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="list-intelligence-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="list-intelligence-header">
          <button onClick={onClose} className="list-intelligence-back">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2>List Intelligence</h2>
          <div style={{ width: 24 }} />
        </div>

        {/* Content */}
        <div className="list-intelligence-content">
          {/* Move Completed to Bottom Card */}
          <div className="intelligence-card">
            <div className="intelligence-card-row">
              <div className="intelligence-card-info">
                <h3>Move Completed to Bottom</h3>
                <p>Keep checked-off items at the bottom of the list.</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={moveCompletedLast}
                  onChange={() => setMoveCompletedLast(!moveCompletedLast)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* AI Provider Card */}
          <div className="intelligence-card">
            <div className="intelligence-card-row">
              <div className="intelligence-card-info">
                <h3>AI Model</h3>
                <p>Choose the AI model for processing your list items.</p>
              </div>
              <select
                value={aiProvider}
                onChange={(e) => onAiProviderChange(e.target.value)}
                className="intelligence-select"
              >
                <option value="gemini-2.5-flash-lite">Standard</option>
                <option value="gemini-2.5-flash">Premium</option>
              </select>
            </div>
          </div>

          {/* Recipe Prompts Card */}
          <div className="intelligence-card">
            <h3 className="intelligence-card-title">Custom Prompts</h3>
            <p className="intelligence-card-description">
              Add instructions for AI to process your items (e.g., add translations, departments, quantities).
            </p>

            <div className="prompts-list-new">
              {prompts.map((prompt, index) => {
                const promptText = prompt.text || prompt;
                const promptPropertyName = prompt.propertyName || '';
                const promptColor = prompt.color || '#3b82f6';

                return (
                  <div key={prompt.id || index} className="prompt-card">
                    {editingIndex === index ? (
                      <div className="prompt-edit-form">
                        <div className="prompt-edit-row">
                          <input
                            type="color"
                            value={editingColor}
                            onChange={(e) => setEditingColor(e.target.value)}
                            className="prompt-color-picker"
                            title="Choose tag color"
                          />
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <input
                              type="text"
                              value={editingPropertyName}
                              onChange={(e) => setEditingPropertyName(e.target.value)}
                              placeholder="Property name (e.g., department, emoji)"
                              className="prompt-property-input"
                            />
                            <textarea
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="prompt-edit-input"
                              placeholder="Full instruction for AI"
                              rows={2}
                            />
                          </div>
                        </div>
                        <div className="prompt-edit-actions">
                          <button onClick={handleCancelEdit} className="prompt-cancel-btn">Cancel</button>
                          <button onClick={handleSaveEdit} className="prompt-save-btn">Save</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="prompt-card-content">
                          <div
                            className="prompt-color-indicator"
                            style={{ backgroundColor: promptColor }}
                            title="Tag color"
                          />
                          <div>
                            <div className="prompt-property-name">{promptPropertyName}</div>
                            <span className="prompt-card-text">"{promptText}"</span>
                          </div>
                        </div>
                        <div className="prompt-card-actions">
                          <button onClick={() => handleEditPrompt(index)} className="prompt-edit-btn">Edit</button>
                          <button onClick={() => handleRemovePrompt(index)} className="prompt-delete-btn">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              {/* Add New Prompt */}
              <div className="add-prompt-section">
                <div className="prompt-add-row">
                  <input
                    type="color"
                    value={newPromptColor}
                    onChange={(e) => setNewPromptColor(e.target.value)}
                    className="prompt-color-picker"
                    title="Choose tag color"
                  />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input
                      type="text"
                      value={newPromptPropertyName}
                      onChange={(e) => setNewPromptPropertyName(e.target.value)}
                      placeholder="Property name (e.g., department, emoji, priority)"
                      className="prompt-property-input"
                    />
                    <textarea
                      value={newPrompt}
                      onChange={(e) => setNewPrompt(e.target.value)}
                      placeholder="Full instruction for AI (e.g., Add a property called 'department' that categorizes items)"
                      className="add-prompt-input"
                      rows={2}
                    />
                  </div>
                </div>
                <button
                  onClick={handleAddPrompt}
                  className="add-prompt-btn-new"
                  disabled={!newPrompt.trim() || !newPromptPropertyName.trim()}
                >
                  + Add Prompt
                </button>
              </div>
            </div>

            {itemsCount === 0 && (
              <p className="no-items-warning-new">No items in this list yet.</p>
            )}
          </div>

          {/* Share Settings (extra content) */}
          {extraContent && (
            <div className="intelligence-card">
              {extraContent}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="list-intelligence-footer">
          <button onClick={onClose} className="intelligence-btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleProcessItems}
            className="intelligence-btn-primary"
            disabled={isProcessing || itemsCount === 0 || prompts.length === 0}
          >
            {isProcessing ? 'Processing...' : 'Run AI'}
          </button>
          <button onClick={handleSave} className="intelligence-btn-save">
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomPromptModal;
